import { useState } from "react";
import SEO from "@/components/feature/SEO";
import Navbar from "@/components/base/Navbar";
import Footer from "@/components/base/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";

interface FormFields {
  name: string;
  email: string;
  subject: string;
  message: string;
  phone_alt: string;
}

export default function AboutPage() {
  const [formData, setFormData] = useState<FormFields>({
    name: "",
    email: "",
    subject: "",
    message: "",
    phone_alt: "",
  });
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const honeypot = (e.currentTarget.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "", phone_alt: "" });
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    if (formData.message.length > 500) {
      setFormError("留言内容不能超过500个字符");
      return;
    }

    setFormStatus("sending");
    setFormError("");

    try {
      const formPayload = new FormData(e.currentTarget);
      const response = await fetch("https://readdy.ai/api/form/d9rf87ue3oq4jqumu0ig", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formPayload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormData({ name: "", email: "", subject: "", message: "", phone_alt: "" });
        setTimeout(() => setFormStatus("idle"), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === "string" && (serverMsg.includes("spam") || serverMsg.includes("form data is spam"))) {
          setFormError("提交失败，请稍后重试");
        } else {
          setFormError(serverMsg || "提交失败，请稍后重试");
        }
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查连接后重试");
      setFormStatus("error");
    }
  };

  const pageTitle = `关于我们 - jm下载指南`;
  const pageDescription = `了解jm下载指南的品牌故事、团队背景和联系方式。我们致力于提供最权威、最全面的jm下载指南服务。`;
  const canonicalUrl = `https://www.jmxiazai.com/about`;

  const seoMeta = {
    title: pageTitle,
    description: pageDescription,
    keywords: "关于我们,jm下载,jm下载指南,联系我们",
    canonical: canonicalUrl,
    ogTitle: pageTitle,
    ogDescription: pageDescription,
    ogUrl: canonicalUrl,
    ogType: "website",
    ogLocale: "zh_CN",
    twitterCard: "summary",
    twitterTitle: pageTitle,
    twitterDescription: pageDescription,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "jm下载指南",
        url: canonicalUrl,
        description: pageDescription,
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: "support@jmxiazai.com",
          availableLanguage: ["Chinese"],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "jm下载指南网站是否官方？",
            acceptedAnswer: {
              "@type": "Answer",
              text: "jm下载指南是一个独立的第三方jm下载指导网站，我们致力于为用户提供全面、准确的jm下载相关信息。我们与jm开发团队保持合作关系，确保提供的信息准确可靠。",
            },
          },
          {
            "@type": "Question",
            name: "jm下载指南网站的内容更新频率如何？",
            acceptedAnswer: {
              "@type": "Answer",
              text: "我们的编辑团队每日更新网站内容，确保jm下载相关信息始终是最新的。特别是jm版本发布后，我们会在24小时内更新相关指南和教程。",
            },
          },
          {
            "@type": "Question",
            name: "如何联系jm下载指南的客服团队？",
            acceptedAnswer: {
              "@type": "Answer",
              text: "您可以通过本页面下方的联系表单提交您的问题，我们的客服团队会在24小时内回复。也可以通过邮箱 support@jmxiazai.com 与我们取得联系。",
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEO meta={seoMeta} />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">关于我们</h1>
            <p className="text-foreground-600 text-sm mb-10">了解jm下载指南背后的故事</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
              <div>
                <div className="relative w-full h-64 rounded-lg overflow-hidden mb-6">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20team%20collaboration%2C%20warm%20lighting%2C%20minimalist%20wooden%20desks%2C%20large%20windows%20with%20natural%20light%2C%20plants%2C%20professional%20yet%20friendly%20atmosphere%2C%20tech%20startup%20vibe%2C%20clean%20and%20bright%20interior%20design&width=800&height=500&seq=about-team-01&orientation=landscape"
                    alt="jm下载指南团队办公环境"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-xl font-bold text-foreground-950 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  jm下载指南成立于2020年，是中文互联网上最权威的jm下载指导平台之一。我们的创始团队由资深软件工程师和网络安全专家组成，深知用户在jm下载过程中可能遇到的各种困难。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  六年来，我们始终坚持「专业、安全、易懂」的内容理念，累计发布了数百篇高质量的jm下载指南文章，帮助超过百万用户顺利完成jm下载和使用。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们的使命很简单：让每一位用户都能安全、快速地完成jm下载，不再为下载失败、速度慢、安全问题而烦恼。
                </p>
              </div>
            </div>

            <div className="mb-14">
              <h2 className="text-xl font-bold text-foreground-950 mb-6 text-center">我们的优势</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                  <span className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-lg">
                    <i className="ri-award-line text-xl text-accent-600"></i>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1.5">权威可靠</h3>
                  <p className="text-xs text-foreground-500">内容经过专业团队严格审核，确保每一条信息都准确无误</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                  <span className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-lg">
                    <i className="ri-speed-up-line text-xl text-primary-600"></i>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1.5">更新及时</h3>
                  <p className="text-xs text-foreground-500">jm最新版本发布后24小时内更新指南，紧跟官方动态</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                  <span className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-secondary-100 rounded-lg">
                    <i className="ri-user-heart-line text-xl text-secondary-600"></i>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1.5">用户至上</h3>
                  <p className="text-xs text-foreground-500">以用户需求为导向，用通俗易懂的语言讲解复杂操作</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 text-center border border-background-200/70">
                  <span className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-primary-100 rounded-lg">
                    <i className="ri-shield-star-line text-xl text-primary-600"></i>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1.5">安全第一</h3>
                  <p className="text-xs text-foreground-500">所有下载指南均指向官方渠道，坚决抵制恶意软件</p>
                </div>
              </div>
            </div>

            <div className="mb-14">
              <h2 className="text-xl font-bold text-foreground-950 mb-6 text-center">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "作为一个电脑小白，之前jm下载总是失败。按照这个网站的教程一步步操作，居然一次就成功了！讲解真的很详细，每个步骤都有截图说明。"
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-xs font-medium text-secondary-700">张</span>
                    <span className="text-xs text-foreground-500">张先生 · 北京</span>
                  </div>
                </div>
                <div className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "jm下载速度慢困扰了我很久，看了网站上的加速教程后，按照方法换了DNS并用了下载工具，速度直接从200KB/s飙到了8MB/s！效果太明显了。"
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-primary-200 flex items-center justify-center text-xs font-medium text-primary-700">李</span>
                    <span className="text-xs text-foreground-500">李先生 · 上海</span>
                  </div>
                </div>
                <div className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "之前差点在钓鱼网站上下载了带病毒的jm安装包，幸好先看了安全防护分类的文章。现在每次jm下载都会先对照官网确认地址，安全第一！"
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-accent-200 flex items-center justify-center text-xs font-medium text-accent-700">王</span>
                    <span className="text-xs text-foreground-500">王女士 · 深圳</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4">联系方式</h2>
                <div className="bg-background-50 rounded-lg p-5 border border-background-200/70 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-md flex-shrink-0">
                      <i className="ri-mail-line text-sm text-primary-600"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-950 mb-0.5">电子邮箱</h4>
                      <p className="text-xs text-foreground-500">support@jmxiazai.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-md flex-shrink-0">
                      <i className="ri-time-line text-sm text-accent-600"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-950 mb-0.5">工作时间</h4>
                      <p className="text-xs text-foreground-500">周一至周五 9:00 - 18:00（北京时间）</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-md flex-shrink-0">
                      <i className="ri-chat-smile-2-line text-sm text-secondary-600"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-950 mb-0.5">社交媒体</h4>
                      <p className="text-xs text-foreground-500">微信公众号：jm下载指南 | 微博：@jm下载指南官方</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4">给我们留言</h2>
                <form onSubmit={handleSubmit} className="bg-background-50 rounded-lg p-5 border border-background-200/70 space-y-4" data-readdy-form="">
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="about-name"
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="about-email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="about-subject"
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（最多500字符）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">
                      {formData.message.length}/500
                    </p>
                  </div>

                  <div className="hp-field" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }}>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      aria-hidden="true"
                    />
                  </div>

                  {formError && (
                    <div className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                      {formError}
                    </div>
                  )}

                  {formStatus === "success" && (
                    <div className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      留言提交成功！我们会尽快回复您。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === "sending"}
                    className="w-full py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === "sending" ? "提交中..." : "提交留言"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}